import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { StreamCard } from "../components/StreamCard";
import { EmptyState } from "../components/EmptyState";
import { useCatalog } from "../contexts/CatalogContext";
import { useScrollRestoration } from "../lib/scrollRestoration";
import type { Stream } from "../lib/types";

const norm = (s: string) =>
  s.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");

export default function SearchPage() {
  const { catalog, activePlaylist } = useCatalog();
  // Local state owns the input value so each keystroke is instant. The URL
  // (?q=…) is mirrored on a debounce — that lets back-navigation from a
  // result land on the same query without round-tripping the React Router
  // history on every character. Routing the input through searchParams
  // directly was making the input feel sluggish on phones and could even
  // drop letters: the URL update is a re-render through the Router context,
  // and on a slow device React would render the input with a stale `value`
  // for a frame, which the browser reads as "user deleted a char".
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = useRef(searchParams.get("q") || "").current;
  const [q, setQ] = useState(initialQ);
  const dq = useDeferredValue(q);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (!q) sp.delete("q");
          else sp.set("q", q);
          return sp;
        },
        { replace: true },
      );
    }, 250);
    return () => window.clearTimeout(handle);
  }, [q, setSearchParams]);

  useScrollRestoration(!!q);

  const results = useMemo(() => {
    if (!catalog || !dq.trim()) return null;
    const needle = norm(dq.trim());
    const match = (s: Stream) => norm(s.name || "").includes(needle);
    return {
      live: catalog.live.streams.filter(match).slice(0, 60),
      vod: catalog.vod.streams.filter(match).slice(0, 60),
      series: catalog.series.streams.filter(match).slice(0, 60),
    };
  }, [catalog, dq]);

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState title="Pick a playlist" hint="Search runs against the active playlist's catalog." cta={{ to: "/settings", label: "Open settings" }} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary mb-4">
          Search
        </h1>
        <div className="relative max-w-2xl mb-6">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            autoFocus
            placeholder="Search across live, movies, series…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-12 bg-surface border border-muted/40 rounded-xl pl-12 pr-4 text-[15px] font-ui text-text-primary placeholder:text-text-muted gold-focus transition-fast"
          />
        </div>

        {!q.trim() ? (
          <p className="text-[14px] font-ui text-text-secondary">
            Type to search across the entire catalog.
          </p>
        ) : !results ? (
          <p className="text-[14px] font-ui text-text-secondary">Loading…</p>
        ) : (
          <div className="space-y-8">
            <Section title="Live TV" items={results.live} layout="landscape" />
            <Section title="Movies" items={results.vod} layout="portrait" />
            <Section title="Series" items={results.series} layout="portrait" hrefFor={(s) => `/series/${encodeURIComponent(s.id)}`} />
            {results.live.length + results.vod.length + results.series.length === 0 && (
              <p className="text-[14px] font-ui text-text-secondary">No matches.</p>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

function Section({
  title,
  items,
  layout,
  hrefFor,
}: {
  title: string;
  items: Stream[];
  layout: "landscape" | "portrait";
  hrefFor?: (s: Stream) => string;
}) {
  if (items.length === 0) return null;
  return (
    <div>
      <h2 className="font-ui font-semibold text-[14px] uppercase tracking-[0.04em] text-text-secondary mb-3">
        {title} <span className="text-text-muted">({items.length})</span>
      </h2>
      <div
        className={`grid gap-3 ${
          layout === "landscape"
            ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5"
            : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8"
        }`}
      >
        {items.map((s) => (
          <StreamCard key={`${s.kind}-${s.id}`} stream={s} layout={layout} href={hrefFor?.(s)} />
        ))}
      </div>
    </div>
  );
}
